/* eslint-disable spaced-comment */

interface Analytics {
  track: (
    eventName: string,
    properties: Record<string, string | number | boolean | null | undefined>,
  ) => void;
}

/**
 * Safely track an analytics event
 * @param eventName - The name of the event to track
 * @param properties - The properties to send with the event
 */
export function trackAnalyticsEvent(
  eventName: string,
  properties: Record<string, string | number | boolean | null | undefined>,
): void {
  const analytics = window?.analytics as Analytics | undefined;

  if (analytics) {
    analytics.track(eventName, properties);
  }
}

/******************************************************************************************
 *                                   Analytics Catalog                                    *
 ******************************************************************************************
 *
 * This module catalogs the analytics events we use in recruiter-ui, and exports
 * helpers to maintain an event naming convention. In particular, this module
 * enforces the convention that PostHog recommends here:
 * https://posthog.com/docs/product-analytics/best-practices.
 *
 * Events are broken down into Categories, Objects, and Actions.
 *
 * A Category describes the context an event took place in: `account_settings`,
 * `signup_flow`. Probably smaller than all but the smallest products, might be
 * larger than a page.
 *
 * An Object is a component or location that the event took place in:
 * `forgot_password_button`, `pricing_page`.
 *
 * Categories and Objects in the catalog have associated documentation and owners so
 * that we can identify why they were added and who we have to talk to about
 * changing or removing them.
 *
 * Actions come from a specified list to prevent them from drifting indiscriminately
 */

/**
 * The full set of action suffixes we support.
 *
 * Please add to this list sparingly.
 */
type AnalyticsAction =
  | 'click'
  | 'submit'
  | 'create'
  | 'view'
  | 'add'
  | 'invite'
  | 'update'
  | 'delete'
  | 'remove'
  | 'start'
  | 'end'
  | 'cancel'
  | 'fail'
  | 'generate'
  | 'send'
  | 'copy'
  | 'download'
  | 'change'
  | 'drill';

/************** TYPE WIZARDRY **********************************************************************************/
// you are not expected to understand this
// It exists to give you nice errors when you write down the wrong event names

interface AnalyticsObject<T extends string = string> {
  readonly name: T;
  readonly doc: string;
  readonly owner: string;
}

interface AnalyticsCategory<
  T extends string = string,
  Objects extends readonly AnalyticsObject[] = readonly AnalyticsObject[],
> {
  readonly name: T;
  readonly doc: string;
  readonly owner: string;
  readonly objects: Objects;
}

interface AnalyticsCatalog<
  Categories extends readonly AnalyticsCategory[] =
    readonly AnalyticsCategory[],
> {
  readonly categories: Categories;
}

type CategoryForName<
  S extends string,
  Categories extends readonly AnalyticsCategory[],
> = Categories extends readonly []
  ? never
  : Categories extends readonly [infer Category, ...infer Rest]
    ? Category extends AnalyticsCategory
      ? S extends Category['name']
        ? Category
        : Rest extends readonly AnalyticsCategory[]
          ? CategoryForName<S, Rest>
          : never
      : never
    : never;

type ParseAnalyticsEventName<
  S extends string,
  Catalog extends AnalyticsCatalog,
> = S extends `${infer Prefix}_${AnalyticsAction}`
  ? Prefix extends `${infer CategoryName}:${infer ObjectName}`
    ? CategoryName extends Catalog['categories'][number]['name']
      ? CategoryForName<
          CategoryName,
          Catalog['categories']
        > extends AnalyticsCategory<string, infer Objects>
        ? Objects extends readonly AnalyticsObject[]
          ? ObjectName extends Objects[number]['name']
            ? S
            : {
                error: `Object named ${ObjectName} does not belong to category ${CategoryName}`;
              }
          : { error: `No Objects for category ${CategoryName}` }
        : {
            error: `Could not find category name ${CategoryName}`;
          }
      : {
          error: `Provided event name did not use a legal category name`;
        }
    : {
        error: `Provided event name did not have 'Category:Object' format`;
      }
  : {
      error: `Provided event name did not end with one of accepted action suffixes, see: ${AnalyticsAction}`;
    };

/*********** END TYPE WIZARDRY *********************************************************************************/

/*****************************************************************************************
 *                             CARE and KEEPING                                          *
 *                                    of                                                 *
 *                            The EVENT CATALOG                                          *
 *****************************************************************************************
 *
 * 1. Categories don't nest in categories, objects don't nest in objects, so pick your
 *    category and object names carefully
 *
 * 2. Renaming categories and objects will break existing code and make it
 *    hard to track the history of the catalog. Do so sparingly, prefer to leave
 *    notes in docs and create new events with different names
 *
 * 3. Please provide good docs. Tell us how to find a category, tell us where to
 *    look for an object. Tell us why it was added, whether it's intended to keep
 *    it forever or if it's temporary, and what time or under what conditions it
 *    could be removed. Our future selves will thank us.
 *
 * 4. If a category or object has events that stretch across multiple products
 *    or multiple UI areas, don't duplicate them unless totally necessary. We
 *    already track product and UI area on events, so the name doesn't need to
 *    reflect where the event occurred.
 */
export const eventCatalog = {
  categories: [
    {
      name: 'example_workflow',
      doc: 'An example category to illustrate how categories should work',
      owner: 'Hire Example Team',
      objects: [
        {
          name: 'send_now_button',
          doc: 'The button that triggers sending things to people',
          owner: 'Hire Example Team',
        },
        {
          name: 'another_button',
          doc: 'The button that just hangs out being a button',
          owner: 'Hire Example Team',
        },
      ],
    },
    {
      name: 'calendar',
      doc: 'Events related to the Calendar feature, including creating and editing calendar sessions (group interview events)',
      owner: 'Hire',
      objects: [
        {
          name: 'availability_slot',
          doc: 'A calendar availability slot (time slot for a group interview event) managed via /calendar/new and /calendar/:id/edit',
          owner: 'Hire',
        },
      ],
    },
    {
      name: 'compliance',
      doc: 'Events for Compliance Stage surfaces in the recruiter app (status labels on the applicant table and drawer)',
      owner: 'Hire',
      objects: [
        {
          name: 'status_label_tooltip',
          doc: 'The per-requirement breakdown tooltip shown when hovering a compliance status label (CMP-1697)',
          owner: 'Hire',
        },
      ],
    },
    {
      name: 'merge_keys',
      doc: 'Events for the Merge Keys feature',
      owner: 'Hire',
      objects: [
        {
          name: 'settings',
          doc: 'The Translation Settings page is visited and the Merge Keys component is shown',
          owner: 'Hire',
        },
      ],
    },
    {
      name: 'custom_terminology',
      doc: 'Events for the Custom Terminology feature',
      owner: 'Hire',
      objects: [
        {
          name: 'settings',
          doc: 'The Translation Settings page is visited and the Custom Terminologies component is shown',
          owner: 'Hire',
        },
        {
          name: 'upload',
          doc: 'The Custom Terminology CSV file upload flow on the Translation Settings page',
          owner: 'Hire',
        },
        {
          name: 'upload_replace',
          doc: 'The replace file action on the Custom Terminology file preview on the Translation Settings page',
          owner: 'Hire',
        },
      ],
    },
    {
      name: 'opening_attributes',
      doc: 'Events on the Opening Attributes (custom fields) settings list. Tracks engagement with the v2.1 usage counts (openings/locations/positions) shown per attribute.',
      owner: 'Hire',
      objects: [
        {
          name: 'edit_button',
          doc: 'The pencil/edit icon on each attribute row in the Opening Attributes list',
          owner: 'Hire',
        },
        {
          name: 'attribute_type',
          doc: 'An attribute type (custom field). Fired when an attribute type is created or deleted via the AttributeTypeForm modal.',
          owner: 'Hire',
        },
        {
          name: 'attribute_name',
          doc: 'The name field of an attribute type. Fired when an attribute name is changed via the AttributeTypeForm modal.',
          owner: 'Hire',
        },
        {
          name: 'attribute_value',
          doc: 'An allowed value of an attribute type. Fired when a value is edited inline or deleted via the AttributeValuesForm.',
          owner: 'Hire',
        },
      ],
    },
    {
      name: 'workflows',
      doc: 'Events in the Workflow Editor, including Cue CTAs in the stages sidebar',
      owner: 'Hire',
      objects: [
        {
          name: 'cue_edit_workflow_button',
          doc: 'The "Edit workflow with Cue" CTA in the Workflow Editor stage column header',
          owner: 'Hire',
        },
      ],
    },
    {
      name: 'omni',
      doc: 'Events forwarded from the embedded Omni analytics dashboard iframe via postMessage. See https://docs.omni.co/embed/events/consume',
      owner: 'Hire',
      objects: [
        {
          name: 'dashboard',
          doc: 'The full Omni embedded dashboard (download, export)',
          owner: 'Hire',
        },
        {
          name: 'dashboard_filter',
          doc: 'A dashboard filter interaction (changed or applied)',
          owner: 'Hire',
        },
        {
          name: 'dashboard_tile',
          doc: 'A single tile/chart on the dashboard (download, drill)',
          owner: 'Hire',
        },
        {
          name: 'page',
          doc: 'Page-level navigation within the embedded Omni iframe',
          owner: 'Hire',
        },
      ],
    },
  ],
} as const;

export type AnalyticsEvent<S extends string> =
  ParseAnalyticsEventName<S, typeof eventCatalog> extends { error: infer Error }
    ? Error extends string
      ? Error
      : 'Unknown error parsing event name'
    : S;
